/**
 * Central Model Configuration
 *
 * Single source of truth for all AI models and providers in the UI.
 * This file defines the 6 active models used throughout the application.
 *
 * IMPORTANT: This is the ONLY place where model lists should be defined.
 * All UI components should import from this file instead of hardcoding models.
 *
 * Last Updated: 2025-10-31 (Phase 3: UI Consistency)
 */

export interface ModelConfig {
  id: string;
  name: string;
  provider: string;
  tier: 'free' | 'pro';
  selected?: boolean;
  logo: string;
  speed?: 'fast' | 'medium' | 'slow';
}

export interface ProviderBranding {
  logoSrc: string;
  alt: string;
}

/**
 * The 6 Active Models
 *
 * These are the production models that users can select for comparisons.
 * Order matters - it defines the default display order in the UI.
 */
export const SUPPORTED_MODELS: ModelConfig[] = [
  {
    id: 'gpt-4',
    name: 'GPT-4',
    provider: 'OpenAI',
    tier: 'pro',
    selected: true,  // Default selected
    logo: '/logos/OpenAI-white-logo.png',
    speed: 'medium'
  },
  {
    id: 'claude-3',
    name: 'Claude 3',
    provider: 'Anthropic',
    tier: 'free',
    selected: false,
    logo: '/logos/Claude-logo.png',
    speed: 'fast'
  },
  {
    id: 'gemini-pro',
    name: 'Gemini Pro',
    provider: 'Google',
    tier: 'free',
    selected: false,
    logo: '/logos/Google-Gemini-logo.png',
    speed: 'medium'
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    provider: 'DeepSeek',
    tier: 'pro',
    selected: false,  // Not default selected
    logo: '/logos/deepseek-logo.png',
    speed: 'fast'
  },
  {
    id: 'llama-3',
    name: 'Llama 3',
    provider: 'Meta',
    tier: 'pro',
    selected: false,
    logo: '/logos/MetaLogo.png',
    speed: 'medium'
  },
  {
    id: 'grok',
    name: 'Grok',
    provider: 'xAI',
    tier: 'pro',
    selected: false,
    logo: '/logos/Grok-logo.png',
    speed: 'fast'
  }
];

/**
 * Provider Branding Map
 *
 * Logo paths and alt text for all providers.
 * Used by ComparisonSetupPage and other components that need provider logos.
 */
export const PROVIDER_BRANDING: Record<string, ProviderBranding> = {
  OpenAI: {
    logoSrc: '/logos/OpenAI-dark-logo.png',
    alt: 'OpenAI logo'
  },
  Anthropic: {
    logoSrc: '/logos/Claude-logo.png',
    alt: 'Anthropic Claude logo'
  },
  Google: {
    logoSrc: '/logos/Google-Gemini-logo.png',
    alt: 'Google Gemini logo'
  },
  xAI: {
    logoSrc: '/logos/Grok-logo.png',
    alt: 'Grok logo'
  },
  DeepSeek: {
    logoSrc: '/logos/deepseek-logo.png',
    alt: 'DeepSeek logo'
  },
  Meta: {
    logoSrc: '/logos/MetaLogo.png',
    alt: 'Meta Llama logo'
  }
};

/**
 * Model Color Map
 *
 * Color assignments for charts, graphs, and visualizations.
 * Consistent colors across all UI components.
 */
export const MODEL_COLORS: Record<string, string> = {
  'gpt-4': '#3B82F6',        // Blue
  'gpt-4o': '#1E40AF',       // Darker Blue
  'claude': '#EF4444',       // Red
  'claude-3': '#DC2626',     // Darker Red
  'gemini': '#14B8A6',       // Teal
  'gemini-pro': '#0F766E',   // Darker Teal
  'deepseek': '#A855F7',     // Purple
  'llama-3': '#F59E0B',      // Amber
  'grok': '#10B981',         // Green
  'mistral': '#8B5CF6',      // Purple
  'cohere': '#F59E0B',       // Amber
  'default': '#64748B'       // Slate
};

/**
 * Helper Functions
 */

/**
 * Get model configuration by ID
 */
export function getModelById(id: string): ModelConfig | undefined {
  return SUPPORTED_MODELS.find(m => m.id === id);
}

/**
 * Get all models for a specific provider
 */
export function getModelsByProvider(provider: string): ModelConfig[] {
  return SUPPORTED_MODELS.filter(m => m.provider === provider);
}

/**
 * Get all default selected models
 */
export function getDefaultModels(): ModelConfig[] {
  return SUPPORTED_MODELS.filter(m => m.selected);
}

/**
 * Get model color (with fallback)
 */
export function getModelColor(modelId: string): string {
  return MODEL_COLORS[modelId] || MODEL_COLORS['default'];
}

/**
 * Get provider branding
 */
export function getProviderBranding(provider: string): ProviderBranding | undefined {
  return PROVIDER_BRANDING[provider];
}

/**
 * Check if model is supported
 */
export function isModelSupported(modelId: string): boolean {
  return SUPPORTED_MODELS.some(m => m.id === modelId);
}

/**
 * Get provider logo path
 */
export function getProviderLogo(provider: string): string {
  return PROVIDER_BRANDING[provider]?.logoSrc || '';
}
